import React, { useEffect, useState } from "react"
import { connect } from "react-redux";
import AddDeviceStep1 from "./AddDeviceStep1";
import { debug } from "../../utils";
import * as modalDataService from "./modalDataService"
import * as validationService from "./validationService"
import AddDeviceStep2 from "./AddDeviceStep2";
import { AddDeviceStep4 } from "./AddDeviceStep4";
import CSSTransition from "react-transition-group/cjs/CSSTransition";
import AddDeviceModalFooter from "./AddDeviceModalFooter";
import { AddDeviceStep3 } from "./AddDeviceStep3";
import * as licenseService from "../../license/licenseService";
import * as deviceService from "../deviceService";
import { Bell, Check, FileText, Network, TestTubeDiagonal } from "lucide-react";

export const STEPS = [
    AddDeviceStep1,
    AddDeviceStep2,
    AddDeviceStep3,
    AddDeviceStep4
]

const STEP_META = [
    {
        key: "device",
        heading: "Add New Device",
        subtitle: "Step 1: Network Configuration & Authentication",
        label: "Device Information",
        helper: "Network & Auth",
        icon: Network
    },
    {
        key: "connection",
        heading: "Add Device - Connection Test",
        subtitle: "Step 2: Connection Status and Configuration Retrieval",
        label: "Connection Test",
        helper: "Verify connectivity",
        icon: TestTubeDiagonal
    },
    {
        key: "syslog",
        heading: "Add Device - Syslog Events",
        subtitle: "Step 3: Configure Syslog Event Monitoring",
        label: "Syslog Events",
        helper: "Configure events",
        icon: FileText
    },
    {
        key: "notifications",
        heading: "Add Device - Notifications",
        subtitle: "Step 4: Syslog Configuration and Alert Settings",
        label: "Notifications",
        helper: "Alert settings",
        icon: Bell
    }
]

function AddDeviceModal({ step, showModal, hostName, mngtIpAddress }) {

    const [modal, setModal] = useState(false);
    const [blocked, setBlocked] = useState(false);
    const [licenseChecked, setLicenseChecked] = useState(false);
    const [deviceCount, setDeviceCount] = useState(0);
    const [allowedDevices, setAllowedDevices] = useState(0);
    const bodyRef = React.useRef(null);
    const autocompleteTokenRef = React.useRef(Date.now());


    useEffect(() => {
        if (showModal) {
            autocompleteTokenRef.current = Date.now();
            refreshLicenseState();
        }
    }, [showModal])

    useEffect(() => {
        if (!showModal || !bodyRef.current) {
            return;
        }

        const disableAutocomplete = () => {
            const inputs = bodyRef.current.querySelectorAll('input');

            inputs.forEach((input, index) => {
                const inputType = (input.getAttribute('type') || 'text').toLowerCase();

                input.setAttribute('autocomplete', inputType === 'password' ? 'new-password' : 'off');
                input.setAttribute('data-lpignore', 'true');
                input.setAttribute('data-form-type', 'other');

                if (!['checkbox', 'radio', 'file'].includes(inputType)) {
                    input.setAttribute('name', `add-device-${autocompleteTokenRef.current}-${index}`);
                }
            });
        };

        disableAutocomplete();
        const animationFrame = window.requestAnimationFrame(disableAutocomplete);

        return () => window.cancelAnimationFrame(animationFrame);
    }, [step, showModal]);

    useEffect(() => {
        if (!showModal || !bodyRef.current) {
            return;
        }

        bodyRef.current.scrollTop = 0;
    }, [step, showModal]);

    const closeModal = () => {
        modalDataService.hideModal();
        validationService.cleanState();
        modalDataService.cleanState();
    }

    const refreshLicenseState = async () => {
        setLicenseChecked(false);

        try {
            const allowed = +await licenseService.getAllowedDevicesCount();
            const allDevices = await deviceService.list();

            setAllowedDevices(allowed);
            setDeviceCount(allDevices.length);
            setBlocked(allDevices.length >= allowed);
            setLicenseChecked(true);
        } catch (error) {
            setAllowedDevices(0);
            setDeviceCount(0);
            setBlocked(true);
            setLicenseChecked(true);
        }
    }

    function renderStep(props) {
        const StepComponent = STEPS[step];
        return <StepComponent {...props} />
    }
    const nodeRef = React.useRef(null);
    const currentStep = STEP_META[step] || STEP_META[0];
    //const ref_modal=React.createRef();
    return (
        <CSSTransition
            nodeRef={nodeRef}
            in={showModal}
            timeout={300}
            onEnter={() => setModal(true)}
            onExited={() => {
                setModal(false);
                validationService.cleanState();
                modalDataService.cleanState();
            }}
            unmountOnExit
            classNames="fade"
        >
            <div className="modal oct_display-block modal-v2-root">
                {modal &&
                    <div className="modal fade oct_modal show modal-v2-overlay">
                        <div className="modal-dialog modal-xl modal-dialog-centered modal-v2-dialog" aria-modal="true">
                            <div className={`modal-content add-device-modal-form modal-v2-shell modal-v2-step-${step + 1}`}>
                                <div className="modal-v2-header">
                                    <div className="modal-v2-header-top">
                                        <div className="modal-v2-header-copy">
                                            <div className="modal-v2-heading">{currentStep.heading}</div>
                                            <div className="modal-v2-subheading">{currentStep.subtitle}</div>
                                        </div>
                                        <button type="button" className="close modal-v2-close" aria-label="Close"
                                            onClick={() => closeModal()}>
                                            <span aria-hidden="true">×</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="modal-v2-stepper-wrap">
                                    <div className="modal-v2-stepper">
                                        {STEP_META.map((item, index) => {
                                            const stepState = index < step ? "is-complete" : index === step ? "is-active" : "is-upcoming";
                                            const StepIcon = item.icon;

                                            return (
                                                <div
                                                    key={item.key}
                                                    className={`modal-v2-stepper-item ${stepState}`}
                                                    data-step={item.key}
                                                >
                                                    <div className="modal-v2-stepper-circle">
                                                        {index < step ? <Check size={22} strokeWidth={3} /> : <StepIcon size={22} strokeWidth={2.4} />}
                                                    </div>
                                                    <div className="modal-v2-stepper-text">
                                                        <div className="modal-v2-stepper-label">{item.label}</div>
                                                        <div className="modal-v2-stepper-helper">{item.helper}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div ref={bodyRef} className="modal-body modal-v2-body">
                                    {step > 0 &&
                                        <div className="modal-v2-summary">
                                            <div className="modal-v2-summary-row">
                                                <div className="modal-v2-summary-label">HOSTNAME</div>
                                                <div className="modal-v2-summary-value">{hostName || ""}</div>
                                            </div>
                                            <div className="modal-v2-summary-row">
                                                <div className="modal-v2-summary-label">IP/FQDN</div>
                                                <div className="modal-v2-summary-value">{mngtIpAddress || ""}</div>
                                            </div>
                                        </div>
                                    }
                                    <div className="container modal-v2-container">
                                        <div className="row modal-v2-content-row">
                                            <div className="col-12 modal-v2-content-column">
                                                {renderStep({ disabled: blocked })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <AddDeviceModalFooter
                                    disabled={blocked}
                                    licenseChecked={licenseChecked}
                                    deviceCount={deviceCount}
                                    allowedDevices={allowedDevices}
                                />
                            </div>
                        </div>
                    </div>
                }
            </div>
        </CSSTransition>

    );
}

function mapStateToProps(state) {
    debug("AddDeviceModal mapStateToProps", state);
    const addDeviceModal = state.addDeviceModal;
    return {
        showModal: addDeviceModal.showModal,
        step: addDeviceModal.step,
        hostName: addDeviceModal.hostName || "",
        mngtIpAddress: addDeviceModal.mngtIpAddress || ""
    };
}

export default connect(mapStateToProps)(AddDeviceModal);
